import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Users, Target } from 'lucide-react';
import { Button } from '../ui/button';

const highlights = [
  'Supply & Manufacturing Equipment',
  'Repair Mechanical & Electrical Parts',
  'Fabricated Metal Products',
  'Oil & Gas Industry Specialist'
];

export const AboutSection = () => {
  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_ima-montaz-preview/artifacts/2br3moia_dji.jpg"
                alt="PT IMA MONTAZ TEKNINDO Workshop"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#001582]/40 to-transparent" />
            </div>
            
            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="absolute -bottom-8 -right-4 md:right-8 bg-white rounded-2xl shadow-xl p-6 border border-slate-100"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#FFC107]/10 rounded-xl flex items-center justify-center">
                  <Award className="w-7 h-7 text-[#FFC107]" />
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-[#001582]">ISO 9001</p>
                  <p className="text-slate-500 text-sm">2015 Certified</p>
                </div>
              </div>
            </motion.div>
            
            {/* Decorative Element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#FFC107]/10 rounded-2xl -z-10" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#001582]/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-[#001582] text-sm font-semibold uppercase tracking-wider">Tentang Kami</span>
            </div>
            
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6 leading-tight">
              Engineering Excellence untuk{' '}
              <span className="text-[#001582]">Industri Indonesia</span>
            </h2>
            
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              PT IMA MONTAZ TEKNINDO adalah perusahaan engineering yang bergerak di bidang 
              supply, manufacture, dan repair mechanical electrical equipment parts serta 
              fabricated metal product untuk industri oil & gas dan sektor industri lainnya.
            </p>
            
            {/* Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {highlights.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#FFC107] flex-shrink-0" />
                  <span className="text-slate-700 text-sm font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
            
            {/* Stats Row */}
            <div className="flex items-center gap-8 pb-8 mb-8 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#001582]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#001582]" />
                </div>
                <div>
                  <p className="font-heading font-bold text-xl text-[#1a1a1a]">100+</p>
                  <p className="text-slate-500 text-xs">Klien Terpercaya</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#001582]/10 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#001582]" />
                </div>
                <div>
                  <p className="font-heading font-bold text-xl text-[#1a1a1a]">5+</p>
                  <p className="text-slate-500 text-xs">Sektor Industri</p>
                </div>
              </div>
            </div>
            
            <Link to="/tentang">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold px-8 py-6 rounded-xl"
                data-testid="about-cta"
              >
                Pelajari Perusahaan
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
